import { generateId } from '@freelancer/datastore/testing/helpers';
import {
  FrontendProjectStatusApi,
  TimeUnitApi,
} from 'api-typings/common/common';
import {
  BidAwardStatusApi,
  BidPaidStatusApi,
  MilestoneStatusApi,
  ProjectDurationApi,
  ProjectStatusApi,
  ProjectSubStatusApi,
  ProjectTypeApi,
} from 'api-typings/projects/projects';
import type { Bid } from '../bids/bids.model';
import {
  CurrencyCode,
  generateCurrencyObject,
  tokenCurrency,
  usdCurrency,
} from '../currencies';
import type { CustomFieldValue } from '../custom-field-info-configurations';
import type {
  PartialProjectBidStats,
  Project,
  ProjectBudget,
  ProjectCollaborations,
  ProjectHourlyProjectInfo,
  ProjectTimeframe,
  ProjectUpgrades,
  ProjectWithOwner,
} from './projects.model';
import { Enterprise, Pool } from '../enterprise/enterprise.model';
import { generateExchangeRateObject } from '../exchange-rates/exchange-rates.seed';
import type { Milestone } from '../milestones/milestones.model';

export interface GenerateProjectOptions {
  readonly ownerId?: number;
  readonly projectId?: number;
  readonly projectType?: ProjectTypeApi;
  readonly currencyCode?: CurrencyCode;
  readonly status?: ProjectStatusApi;
  readonly subStatus?: ProjectSubStatusApi;
  readonly selectedBids?: readonly Bid[];
  readonly budget?: ProjectBudget;
  readonly bidStats?: PartialProjectBidStats;
  readonly local?: boolean;
  readonly hireme?: boolean;
  readonly upgrades?: Partial<ProjectUpgrades>;
  readonly enterpriseIds?: readonly number[];
  readonly hourlyProjectInfo?: ProjectHourlyProjectInfo;
  readonly billingCode?: string;
  readonly title?: string;
  readonly seoUrl?: string;
  readonly frontendProjectStatus?: FrontendProjectStatusApi;
  readonly timeframe?: ProjectTimeframe;
  readonly timeSubmitted?: number;
  readonly language?: string;
  readonly poolIds?: readonly number[];
  readonly isEscrowProject?: boolean;
  readonly customFieldValues?: readonly CustomFieldValue[];
  readonly projectCollaborations?: readonly ProjectCollaborations[];
  readonly groupIds?: readonly number[];
  readonly deleted?: boolean;
  readonly isBuyerKycRequired?: boolean;
}

/**
 * Returns a project object. By default, this is a fixed NHM project that is
 * "Active" aka "Open for Bidding".
 */
export function generateProjectObject(
  params: GenerateProjectOptions & { readonly ownerId: number },
): ProjectWithOwner;
export function generateProjectObject(params: GenerateProjectOptions): Project;
export function generateProjectObject({
  ownerId,
  projectId,
  currencyCode = CurrencyCode.USD,
  projectType = ProjectTypeApi.FIXED,
  status = ProjectStatusApi.ACTIVE,
  subStatus,
  selectedBids = [],
  budget = { minimum: 250, maximum: 750 },
  bidStats = { bidCount: 0, bidAvg: 0 },
  local = false,
  hireme = false,
  upgrades = {},
  enterpriseIds,
  billingCode,
  title = 'A demo project',
  seoUrl,
  frontendProjectStatus = FrontendProjectStatusApi.OPEN,
  timeframe,
  timeSubmitted = Date.now(),
  language = 'en',
  poolIds = [Pool.FREELANCER],
  isEscrowProject = false,
  hourlyProjectInfo,
  customFieldValues,
  projectCollaborations,
  groupIds,
  deleted = false,
  isBuyerKycRequired = false,
}: GenerateProjectOptions): Project | ProjectWithOwner {
  const id = projectId !== undefined ? projectId : generateId();

  const projectUpgrades: ProjectUpgrades = {
    assisted: false,
    featured: false,
    fulltime: false,
    ipContract: false,
    listed: false,
    NDA: false,
    nonpublic: false,
    projectManagement: false,
    qualified: false,
    sealed: false,
    urgent: false,
    extend: false,
    unpaidRecruiter: false,
    pfOnly: false,
    premium: false,
    enterprise: false,
    ...upgrades,
  };

  return {
    id,
    bidPeriod: 7,
    bidStats,
    budget,
    currency: generateCurrencyObject(currencyCode),
    deleted,
    // WARNING: Do not rely on this in tests or in the app. It can be broken for projects
    // that move backwards in the project flow at any point (e.g. cancelled then reawarded),
    // and multi-award. Use ProjectStatusHelper instead.
    frontendProjectStatus,
    hideBids: false,
    hireme,
    language,
    local,
    ownerId,
    previewDescription: 'A long project description',
    projectCollaborations: projectCollaborations ?? [],
    seoUrl: seoUrl ?? `project-${id}`,
    status,
    subStatus,
    timeframe,
    timeSubmitted,
    title,
    type: projectType,
    upgrades: projectUpgrades,
    poolIds,
    enterpriseIds: enterpriseIds ?? [],
    isSellerKycRequired: false, // TODO: T267853 - Cali KYC
    isBuyerKycRequired,
    isDeloitteProject: enterpriseIds
      ? enterpriseIds.includes(Enterprise.DELOITTE_DC)
      : false,
    isInsourceProject:
      currencyCode === 'TKN' && projectType === ProjectTypeApi.FIXED,
    isTokenProject: currencyCode === CurrencyCode.TKN,
    selectedBids: selectedBids.filter(
      // Sadly this is an implementation detail related to REST API call made by
      // the datastore, which excludes cancelled, rejected and revoked bids.
      // Therefore, in order to reflect this in all of our frontend seed data,
      // these bids will never appear as part of the project's selected bids.
      bid =>
        bid.awardStatus === BidAwardStatusApi.PENDING ||
        bid.awardStatus === BidAwardStatusApi.AWARDED,
    ),
    customFieldValues: customFieldValues || [],
    billingCode,
    isEscrowProject,
    hourlyProjectInfo,
    isHpProject: enterpriseIds ? enterpriseIds.includes(Enterprise.HP) : false,
    groupIds,
  };
}

export function fixedProject(): Pick<GenerateProjectOptions, 'projectType'> {
  return {
    projectType: ProjectTypeApi.FIXED,
  };
}

/**
 *
 * Hourly project can be awarded as fixed,
 * in which case we can't rely on the projectType
 * but have to check that hourlyContract is undefined.
 */
const hourlyProject: Pick<
  GenerateProjectOptions,
  'projectType' | 'hourlyProjectInfo' | 'budget'
> = {
  projectType: ProjectTypeApi.HOURLY,
  hourlyProjectInfo: {
    commitment: {
      hours: 40,
      interval: TimeUnitApi.WEEK,
    },
    durationEnum: ProjectDurationApi.UNSPECIFIED,
  },
  budget: { minimum: 15, maximum: 25 },
};

export function fixedUsdProject(): Pick<
  GenerateProjectOptions,
  'projectType' | 'currencyCode'
> {
  return { ...fixedProject(), ...usdCurrency() };
}

export function fixedTokenProject(): Pick<
  GenerateProjectOptions,
  'projectType' | 'currencyCode'
> {
  return { ...fixedProject(), ...tokenCurrency() };
}

export function hourlyUsdProject(): Pick<
  GenerateProjectOptions,
  'projectType' | 'hourlyProjectInfo' | 'currencyCode'
> {
  return { ...hourlyProject, ...usdCurrency() };
}

/** Returns a project that is pending approval by EQA via admin. */
export function pendingProject(): Pick<
  GenerateProjectOptions,
  'status' | 'subStatus' | 'selectedBids'
> {
  return {
    status: ProjectStatusApi.PENDING,
    subStatus: undefined,
    selectedBids: [],
  };
}

/** Returns a project that has been rejected by EQA. */
export function rejectedProject(): Pick<
  GenerateProjectOptions,
  'status' | 'subStatus' | 'selectedBids'
> {
  return {
    status: ProjectStatusApi.REJECTED,
    subStatus: undefined,
    selectedBids: [],
  };
}

/**
 * This seems to be a very specific case of ending a project. An accepted bid can
 * be cancelled (award_status of 'canceled') by ending the project as complete
 * without creating any milestones or invoices.
 *
 * Ending a project this way indicates that the project is no longer needed.
 */
export function cancelledProject(): Pick<
  GenerateProjectOptions,
  'status' | 'subStatus' | 'selectedBids'
> {
  return {
    status: ProjectStatusApi.CLOSED,
    // Don't ask: https://phabricator.tools.flnltd.com/w/team/hiring_experience/feature_specifications/project_states/cancel_seller/
    subStatus: ProjectSubStatusApi.CANCEL_SELLER,
    // If a previously accepted bid is marked as complete with no milestones,
    // it's removed from the selected bids of the project (because it's awardStatus
    // becomes `canceled`). The complete bid can be awarded again, along with
    // other bids on the project, but the project remains closed to further bidding.
    selectedBids: [],
  };
}

/**
 * This refers to the specific case of closing a project **for bidding**, which
 * is only allowed when the project is open for bidding, i.e. is unawarded.
 */
export function closedProject(): Pick<
  GenerateProjectOptions,
  'status' | 'subStatus' | 'selectedBids'
> {
  return {
    status: ProjectStatusApi.FROZEN,
    subStatus: ProjectSubStatusApi.FROZEN_TIMEOUT,
    // Existing bids can still be awarded, after which the project resumes its
    // normal project flow
    selectedBids: [],
  };
}

/**
 * Returns a project that has expired without any bids being awarded. This is
 * different from a project that has been closed for bidding, as the project
 * status is `CLOSED` instead of `FROZEN`.
 */
export function expiredProject(): Pick<
  GenerateProjectOptions,
  'status' | 'subStatus' | 'selectedBids'
> {
  return {
    status: ProjectStatusApi.CLOSED,
    subStatus: ProjectSubStatusApi.CLOSED_EXPIRED,
    selectedBids: [],
  };
}

/**
 * Returns a project that has awarded bids. The bids passed in can be accepted,
 * revoked, or rejected.
 */
export function awardedProject({
  selectedBids = [],
}: {
  readonly selectedBids: readonly Bid[] | undefined;
}): Pick<
  GenerateProjectOptions,
  'status' | 'subStatus' | 'selectedBids' | 'frontendProjectStatus'
> {
  const hasAcceptedBids = selectedBids.some(
    bid => bid.awardStatus === BidAwardStatusApi.AWARDED,
  );
  return {
    status: hasAcceptedBids ? ProjectStatusApi.CLOSED : ProjectStatusApi.FROZEN,
    subStatus: hasAcceptedBids
      ? ProjectSubStatusApi.CLOSED_AWARDED
      : ProjectSubStatusApi.FROZEN_AWARDED,
    // TODO: T237121 - Remove this after removing logic based on frontendProjectStatus from Invite Freelancer modal
    frontendProjectStatus: hasAcceptedBids
      ? FrontendProjectStatusApi.WORK_IN_PROGRESS
      : FrontendProjectStatusApi.OPEN,
    selectedBids,
  };
}

/**
 * Returns a project that is incomplete by ending the accepted bid as incomplete.
 * There's no difference in project and bid statuses between partial payment and
 * no payment.
 *
 * A bid can only be marked as incomplete if it has not been fully paid, since
 * a bid is automatically complete if released milestone value >= bid amount.
 */
// TODO: T267853 - What happens for multi-award?
export function incompleteProject({
  selectedBids = [],
}: {
  readonly selectedBids: readonly Bid[] | undefined;
}): Pick<GenerateProjectOptions, 'status' | 'subStatus' | 'selectedBids'> {
  if (
    selectedBids.some(bid => bid.paidStatus === BidPaidStatusApi.FULLY_PAID)
  ) {
    throw new Error(
      'An incomplete project cannot have fully paid bids. You might be looking for `completeProject` instead.',
    );
  }

  return {
    status: ProjectStatusApi.CLOSED,
    subStatus: ProjectSubStatusApi.CANCEL_SELLER,
    selectedBids,
  };
}

/**
 * Returns a project with a complete bid. This is done by releasing milestones
 * with value >= bid amount (fully paid), or < bid amount (partly paid).
 *
 * NOTE: Confusingly, when a project is "ended" it is actually the bid that is
 * ended. This is important when a project has multiple awarded (selected) bids.
 * If one bid is marked as complete but another has been accepted, the project
 * status on the PVP is still `In-Progress` (see ProjectStatusHelper).
 */
export function completeProject({
  selectedBids = [],
  milestones = [],
}: {
  readonly selectedBids: readonly Bid[] | undefined;
  readonly milestones?: readonly Milestone[];
}): Pick<GenerateProjectOptions, 'status' | 'subStatus' | 'selectedBids'> {
  const hasFullyPaidBid = selectedBids.some(bid => {
    const totalReleasedMilestones = milestones
      .filter(milestone => milestone.bidId === bid.id)
      .filter(milestone => milestone.status === MilestoneStatusApi.CLEARED)
      .reduce((acc, milestone) => acc + (milestone.amount || 0), 0);

    return totalReleasedMilestones >= bid.amount;
  });

  return {
    status: ProjectStatusApi.CLOSED,
    subStatus: hasFullyPaidBid
      ? ProjectSubStatusApi.CLOSED_AWARDED
      : ProjectSubStatusApi.CANCEL_SELLER,
    selectedBids,
  };
}

/**
 * A premium project is one whose average budget is >= 1500 USD, or has a bid
 * with amount >= $1500 USD.
 */
export function premiumProject({
  currencyCode = CurrencyCode.USD,
}: {
  readonly currencyCode?: CurrencyCode;
} = {}): Pick<GenerateProjectOptions, 'budget'> {
  const minimum = 1000;
  const maximum = 2000;
  const usdExchangeRate =
    generateExchangeRateObject(currencyCode).from[CurrencyCode.USD];

  return {
    budget: {
      minimum: Math.ceil(minimum * usdExchangeRate),
      maximum: Math.ceil(maximum * usdExchangeRate),
    },
  };
}

/**
 * A premium verified project is one which has minimum budget >= $2500 USD and requires
 * bidders to be Freelancer Verified.
 */
export function premiumVerifiedProject({
  currencyCode = CurrencyCode.USD,
}: {
  readonly currencyCode?: CurrencyCode;
} = {}): Pick<GenerateProjectOptions, 'budget'> {
  const minimum = 2500;
  const maximum = 5000;
  const usdExchangeRate =
    generateExchangeRateObject(currencyCode).from[CurrencyCode.USD];

  return {
    budget: {
      minimum: Math.ceil(minimum * usdExchangeRate),
      maximum: Math.ceil(maximum * usdExchangeRate),
    },
  };
}

/**
 * A non-premium project (avg. project budget < $1500 USD) that still allows premium bid amounts (>= $1500 USD).
 */
export function largeBudgetNonPremiumProject({
  currencyCode = CurrencyCode.USD,
}: {
  readonly currencyCode?: CurrencyCode;
} = {}): Pick<GenerateProjectOptions, 'budget'> {
  const minimum = 100;
  const maximum = 2000;
  const usdExchangeRate =
    generateExchangeRateObject(currencyCode).from[CurrencyCode.USD];

  return {
    budget: {
      minimum: Math.ceil(minimum * usdExchangeRate),
      maximum: Math.ceil(maximum * usdExchangeRate),
    },
  };
}

/**
 * The sealed and private upgrade applied to deloitte projects.
 */
export function deloitteProjectUpgrades(): Pick<
  GenerateProjectOptions,
  'upgrades'
> {
  return {
    upgrades: {
      assisted: false,
      featured: false,
      fulltime: false,
      ipContract: false,
      listed: false,
      NDA: false,
      nonpublic: true,
      projectManagement: false,
      qualified: false,
      sealed: true,
      urgent: false,
      extend: false,
      unpaidRecruiter: false,
      pfOnly: false,
    },
  };
}

export function enterpriseUpgrade(): Pick<GenerateProjectOptions, 'upgrades'> {
  return {
    upgrades: {
      assisted: true,
      featured: false,
      fulltime: false,
      ipContract: false,
      listed: false,
      NDA: false,
      nonpublic: false,
      projectManagement: false,
      qualified: false,
      sealed: false,
      urgent: false,
      extend: false,
      enterprise: true,
    },
  };
}

export function facebookProject(): Pick<
  GenerateProjectOptions,
  'enterpriseIds'
> {
  return {
    enterpriseIds: [Enterprise.FACEBOOK],
  };
}

/**
 * Creates a non linked deloitte project.
 *
 * This differs from a linked deloitte project as linked deloitte projects have other details that can be added using the
 * linkedInternalProject` mixin from `project-view-projects.seed.ts`.
 * The linked external projects are fixed non token projects and are not in the `deloitte_dc` pool.
 */
export function deloitteInternalProject(): Pick<
  GenerateProjectOptions,
  | 'enterpriseIds'
  | 'poolIds'
  | 'projectType'
  | 'hourlyProjectInfo'
  | 'currencyCode'
> {
  return {
    ...hourlyProject,
    ...tokenCurrency(),
    enterpriseIds: [Enterprise.DELOITTE_DC],
    poolIds: [Pool.DELOITTE_DC],
    ...deloitteProjectUpgrades(),
  };
}

/**
 * Creates a deloitte linked external project.
 *
 * Deloitte linked external projects are fixed non token projects and are in the `deloitte_ftn` pool.
 *
 * Use this together with `linkedExternalProject` mixin from `project-view-projects.seed.ts` which adds other linked
 * project details to represent a Deloitte linked external project.
 */
export function deloitteLinkedExternalProject(): Pick<
  GenerateProjectOptions,
  | 'projectType'
  | 'hourlyProjectInfo'
  | 'currencyCode'
  | 'enterpriseIds'
  | 'poolIds'
> {
  return {
    ...fixedProject(),
    ...usdCurrency(),
    enterpriseIds: [Enterprise.DELOITTE_DC],
    poolIds: [Pool.DELOITTE_FTN],
  };
}

export function hpProject(): Pick<
  GenerateProjectOptions,
  'enterpriseIds' | 'poolIds'
> {
  return {
    enterpriseIds: [Enterprise.HP],
    poolIds: [Pool.HP],
  };
}

export function ongoingProject(): Pick<GenerateProjectOptions, 'timeframe'> {
  return {
    timeframe: {
      startDate: Date.now(),
      endDate: undefined,
    },
  };
}
